import { createContext, ReactNode, useContext, useMemo } from 'react'

export type EditablePath = ReadonlyArray<string | number>

export interface EditableContextValue {
  editable: boolean
  // Called when an editable primitive commits a change. The path identifies
  // the location inside the report payload (e.g. ['sections', 0, 'blocks',
  // 0, 'essence', 'content']) and the value is the new contents.
  onChange: (path: EditablePath, value: unknown) => void
  // Called to grow or shrink a list inside the payload — the rows of a table,
  // the items of a checklist. `path` addresses the list itself and `index` the
  // row the control belongs to: 'add' inserts a blank copy of it right after,
  // 'remove' drops it. The host owns both, because only it holds the draft.
  onListChange?: (path: EditablePath, op: 'add' | 'remove', index: number) => void
}

const noop = () => {}

const defaultValue: EditableContextValue = {
  editable: false,
  onChange: noop,
}

const EditableContext = createContext<EditableContextValue>(defaultValue)

interface ProviderProps {
  editable?: boolean
  onChange: EditableContextValue['onChange']
  onListChange?: EditableContextValue['onListChange']
  children: ReactNode
}

// When admin wraps the report tree with this provider, every Editable* primitive
// inside the tree becomes click-to-edit. user-interface never wraps with the
// provider, so the same primitives render as plain text.
export const EditableProvider = ({
  editable = true,
  onChange,
  onListChange,
  children,
}: ProviderProps) => {
  const value = useMemo(
    () => ({ editable, onChange, onListChange }),
    [editable, onChange, onListChange]
  )
  return <EditableContext.Provider value={value}>{children}</EditableContext.Provider>
}

export const useEditable = () => useContext(EditableContext)
